import { useState } from "react";
import { 
  HelpCircle, 
  Search, 
  LayoutDashboard, 
  Monitor, 
  Bell, 
  FileText, 
  BookOpen, 
  PlayCircle, 
  Play, 
  Mail, 
  MessageCircle, 
  HeadphonesIcon 
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

export default function Help() {
  const [searchQuery, setSearchQuery] = useState("");
  
  // Handle search
  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      console.log(`Searching documentation for: "${searchQuery}"`);
      // In a real app, this would search the documentation
      alert(`Searching for: ${searchQuery}`);
    }
  };

  return (
    <div>
      {/* Page Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center">
          <div>
            <h1 className="text-2xl font-semibold text-neutral-dark">Help & Documentation</h1>
            <p className="mt-1 text-sm text-gray-500">
              User guides, tutorials, and support resources
            </p>
          </div>
          <div className="mt-4 md:mt-0">
            <Button variant="outline">
              <HelpCircle className="h-4 w-4 mr-2" />
              Contact Support
            </Button>
          </div>
        </div>
      </div>

      {/* Search */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <form onSubmit={handleSearch} className="flex space-x-2">
            <Input
              type="text"
              placeholder="Search documentation..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1"
            />
            <Button type="submit">
              <Search className="h-4 w-4 mr-2" />
              Search
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Documentation Tabs */}
      <Tabs defaultValue="guides" className="mb-6">
        <TabsList className="grid grid-cols-2 md:grid-cols-4 w-full">
          <TabsTrigger value="guides">User Guides</TabsTrigger>
          <TabsTrigger value="api">API Documentation</TabsTrigger>
          <TabsTrigger value="faq">FAQ</TabsTrigger>
          <TabsTrigger value="videos">Video Tutorials</TabsTrigger>
        </TabsList>
        
        {/* User Guides Tab */}
        <TabsContent value="guides" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <LayoutDashboard className="h-5 w-5 mr-2 text-primary" />
                  Getting Started
                </CardTitle>
                <CardDescription>Learn the basics of the monitoring platform</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li>
                    <Button variant="link" className="p-0 h-auto text-primary hover:text-primary-dark">
                      Platform Overview
                    </Button>
                  </li>
                  <li>
                    <Button variant="link" className="p-0 h-auto text-primary hover:text-primary-dark">
                      Dashboard Navigation
                    </Button>
                  </li>
                  <li>
                    <Button variant="link" className="p-0 h-auto text-primary hover:text-primary-dark">
                      First-Time Setup Guide
                    </Button>
                  </li>
                  <li>
                    <Button variant="link" className="p-0 h-auto text-primary hover:text-primary-dark">
                      Understanding Alerts
                    </Button>
                  </li>
                  <li>
                    <Button variant="link" className="p-0 h-auto text-primary hover:text-primary-dark">
                      User Permissions
                    </Button>
                  </li>
                </ul>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Monitor className="h-5 w-5 mr-2 text-primary" />
                  Device Management
                </CardTitle>
                <CardDescription>How to manage and monitor devices</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li>
                    <Button variant="link" className="p-0 h-auto text-primary hover:text-primary-dark">
                      Adding New Devices
                    </Button>
                  </li>
                  <li>
                    <Button variant="link" className="p-0 h-auto text-primary hover:text-primary-dark">
                      Device Configuration
                    </Button>
                  </li>
                  <li>
                    <Button variant="link" className="p-0 h-auto text-primary hover:text-primary-dark">
                      Device Groups
                    </Button>
                  </li>
                  <li>
                    <Button variant="link" className="p-0 h-auto text-primary hover:text-primary-dark">
                      Monitoring Settings
                    </Button>
                  </li>
                  <li>
                    <Button variant="link" className="p-0 h-auto text-primary hover:text-primary-dark">
                      Troubleshooting Device Issues
                    </Button>
                  </li>
                </ul>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Bell className="h-5 w-5 mr-2 text-primary" />
                  Alert Management
                </CardTitle>
                <CardDescription>Configure and respond to system alerts</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li>
                    <Button variant="link" className="p-0 h-auto text-primary hover:text-primary-dark">
                      Alert Configuration
                    </Button>
                  </li>
                  <li>
                    <Button variant="link" className="p-0 h-auto text-primary hover:text-primary-dark">
                      Notification Channels
                    </Button>
                  </li>
                  <li>
                    <Button variant="link" className="p-0 h-auto text-primary hover:text-primary-dark">
                      Alert Severity Levels
                    </Button>
                  </li>
                  <li>
                    <Button variant="link" className="p-0 h-auto text-primary hover:text-primary-dark">
                      Creating Alert Rules
                    </Button>
                  </li>
                  <li>
                    <Button variant="link" className="p-0 h-auto text-primary hover:text-primary-dark">
                      Alert Response Procedures
                    </Button>
                  </li>
                </ul>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <FileText className="h-5 w-5 mr-2 text-primary" />
                  Reporting & Analytics
                </CardTitle>
                <CardDescription>Generate and interpret system reports</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li>
                    <Button variant="link" className="p-0 h-auto text-primary hover:text-primary-dark">
                      Creating Custom Reports
                    </Button>
                  </li>
                  <li>
                    <Button variant="link" className="p-0 h-auto text-primary hover:text-primary-dark">
                      Performance Analysis
                    </Button>
                  </li>
                  <li>
                    <Button variant="link" className="p-0 h-auto text-primary hover:text-primary-dark">
                      Trend Identification
                    </Button>
                  </li>
                  <li>
                    <Button variant="link" className="p-0 h-auto text-primary hover:text-primary-dark">
                      Exporting Reports
                    </Button>
                  </li>
                  <li>
                    <Button variant="link" className="p-0 h-auto text-primary hover:text-primary-dark">
                      Scheduled Reports
                    </Button>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        {/* API Documentation Tab */}
        <TabsContent value="api" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>API Documentation</CardTitle>
              <CardDescription>
                Complete reference for the NetMonitor Pro API
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="text-lg font-medium mb-2">Authentication</h3>
                <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
                  <p className="text-sm text-gray-700 mb-2">
                    All API requests require authentication using an API key or OAuth token.
                  </p>
                  <div className="mt-3">
                    <h4 className="text-sm font-medium mb-1">Example Request</h4>
                    <pre className="bg-gray-900 text-gray-100 p-3 rounded text-xs overflow-x-auto">
                      {`curl -X GET https://api.netmonitor.pro/v1/devices \\
  -H "Authorization: Bearer YOUR_API_TOKEN" \\
  -H "Content-Type: application/json"`}
                    </pre>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-medium mb-2">Devices API</h3>
                <div className="space-y-3">
                  <div className="border border-gray-200 rounded-md">
                    <div className="flex items-center justify-between p-3 border-b border-gray-200">
                      <div className="flex items-center">
                        <span className="px-2 py-1 text-xs font-bold rounded bg-green-100 text-green-800 mr-2">GET</span>
                        <span className="font-mono text-sm">/api/devices</span>
                      </div>
                      <p className="text-sm text-gray-500">List all devices</p>
                    </div>
                    <div className="p-3">
                      <Button variant="link" className="p-0 h-auto text-sm text-primary hover:text-primary-dark">
                        View Documentation
                      </Button>
                    </div>
                  </div>
                  
                  <div className="border border-gray-200 rounded-md">
                    <div className="flex items-center justify-between p-3 border-b border-gray-200">
                      <div className="flex items-center">
                        <span className="px-2 py-1 text-xs font-bold rounded bg-green-100 text-green-800 mr-2">GET</span>
                        <span className="font-mono text-sm">/api/devices/:id</span>
                      </div>
                      <p className="text-sm text-gray-500">Get a single device</p>
                    </div>
                    <div className="p-3">
                      <Button variant="link" className="p-0 h-auto text-sm text-primary hover:text-primary-dark">
                        View Documentation
                      </Button>
                    </div>
                  </div>
                  
                  <div className="border border-gray-200 rounded-md">
                    <div className="flex items-center justify-between p-3 border-b border-gray-200">
                      <div className="flex items-center">
                        <span className="px-2 py-1 text-xs font-bold rounded bg-blue-100 text-blue-800 mr-2">POST</span>
                        <span className="font-mono text-sm">/api/devices</span>
                      </div>
                      <p className="text-sm text-gray-500">Create a new device</p>
                    </div>
                    <div className="p-3">
                      <Button variant="link" className="p-0 h-auto text-sm text-primary hover:text-primary-dark">
                        View Documentation
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-medium mb-2">Alerts API</h3>
                <div className="space-y-3">
                  <div className="border border-gray-200 rounded-md">
                    <div className="flex items-center justify-between p-3 border-b border-gray-200">
                      <div className="flex items-center">
                        <span className="px-2 py-1 text-xs font-bold rounded bg-green-100 text-green-800 mr-2">GET</span>
                        <span className="font-mono text-sm">/api/alerts</span>
                      </div>
                      <p className="text-sm text-gray-500">List all alerts</p>
                    </div>
                    <div className="p-3">
                      <Button variant="link" className="p-0 h-auto text-sm text-primary hover:text-primary-dark">
                        View Documentation
                      </Button>
                    </div>
                  </div>
                  
                  <div className="border border-gray-200 rounded-md">
                    <div className="flex items-center justify-between p-3 border-b border-gray-200">
                      <div className="flex items-center">
                        <span className="px-2 py-1 text-xs font-bold rounded bg-blue-100 text-blue-800 mr-2">POST</span>
                        <span className="font-mono text-sm">/api/alerts/:id/acknowledge</span>
                      </div>
                      <p className="text-sm text-gray-500">Acknowledge an alert</p>
                    </div>
                    <div className="p-3">
                      <Button variant="link" className="p-0 h-auto text-sm text-primary hover:text-primary-dark">
                        View Documentation
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="text-center mt-4">
                <Button>
                  <BookOpen className="h-4 w-4 mr-2" />
                  View Full API Documentation
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* FAQ Tab */}
        <TabsContent value="faq" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Frequently Asked Questions</CardTitle>
              <CardDescription>
                Find answers to common questions about the monitoring platform
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="border-b border-gray-200 pb-4">
                  <h3 className="text-lg font-medium mb-2">General Questions</h3>
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-base font-medium text-primary">What is NetMonitor Pro?</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        NetMonitor Pro is a comprehensive network infrastructure monitoring platform
                        that provides real-time visibility into your network devices, servers, and
                        applications. It helps identify and resolve issues quickly to minimize downtime.
                      </p>
                    </div>
                    <div>
                      <h4 className="text-base font-medium text-primary">How does the licensing work?</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        NetMonitor Pro offers subscription-based licensing with different tiers based
                        on the number of devices monitored and features required. Contact our sales team
                        for detailed pricing information.
                      </p>
                    </div>
                    <div>
                      <h4 className="text-base font-medium text-primary">Can I try before buying?</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        Yes, we offer a 30-day free trial with full access to all features. No credit card
                        is required to start the trial. Contact sales for an extended evaluation if needed.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="border-b border-gray-200 pb-4">
                  <h3 className="text-lg font-medium mb-2">Technical Questions</h3>
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-base font-medium text-primary">How do I add a new device?</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        Navigate to the Device Inventory page and click the "Add Device" button in the
                        top right corner. Fill in the required device information such as name, IP address,
                        and device type, then click "Save" to add the device to your monitoring inventory.
                      </p>
                    </div>
                    <div>
                      <h4 className="text-base font-medium text-primary">What protocols are supported?</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        NetMonitor Pro supports a wide range of protocols including SNMP (v1, v2c, v3),
                        ICMP, SSH, WMI, HTTP/S, TCP, and more. This allows monitoring of virtually any
                        network device or server regardless of vendor.
                      </p>
                    </div>
                    <div>
                      <h4 className="text-base font-medium text-primary">How do I configure alert notifications?</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        Go to the Settings page and select the Notifications tab. Here you can configure
                        various notification channels such as email, SMS, and Slack. You can then set up
                        alert rules that specify which types of events trigger notifications through which channels.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-lg font-medium mb-2">Troubleshooting</h3>
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-base font-medium text-primary">Why can't I see data for a device?</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        Check that the device is online and reachable from the monitoring server. Verify your
                        credentials and access permissions are correct. Ensure that any required ports are open
                        in your network firewall. Check the system logs for specific error messages.
                      </p>
                    </div>
                    <div>
                      <h4 className="text-base font-medium text-primary">How do I reset my password?</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        Click on the "Forgot password?" link on the login page. Enter your email address,
                        and you will receive password reset instructions. If you're still having trouble,
                        contact your system administrator or our support team.
                      </p>
                    </div>
                    <div>
                      <h4 className="text-base font-medium text-primary">The dashboard is loading slowly. What can I do?</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        Try adjusting the time range to a shorter period, as displaying large amounts of historical
                        data can affect performance. Check your browser and system resources. If problems persist,
                        contact support as it may indicate a backend performance issue.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Video Tutorials Tab */}
        <TabsContent value="videos" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card>
              <div className="aspect-video bg-gray-200 rounded-t-lg flex items-center justify-center">
                <PlayCircle className="h-16 w-16 text-gray-400" />
              </div>
              <CardHeader className="pt-4">
                <CardTitle className="text-base">Getting Started with NetMonitor Pro</CardTitle>
                <CardDescription>Duration: 8:45</CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="text-sm text-gray-600 mb-3">
                  This tutorial covers the basics of setting up your monitoring environment,
                  navigating the dashboard, and configuring your first device.
                </p>
                <Button variant="outline" className="w-full">
                  <Play className="h-4 w-4 mr-2" />
                  Watch Tutorial
                </Button>
              </CardContent>
            </Card>
            
            <Card>
              <div className="aspect-video bg-gray-200 rounded-t-lg flex items-center justify-center">
                <PlayCircle className="h-16 w-16 text-gray-400" />
              </div>
              <CardHeader className="pt-4">
                <CardTitle className="text-base">Advanced Alert Configuration</CardTitle>
                <CardDescription>Duration: 12:18</CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="text-sm text-gray-600 mb-3">
                  Learn how to create complex alert rules with multiple conditions, custom
                  thresholds, and escalation procedures.
                </p>
                <Button variant="outline" className="w-full">
                  <Play className="h-4 w-4 mr-2" />
                  Watch Tutorial
                </Button>
              </CardContent>
            </Card>
            
            <Card>
              <div className="aspect-video bg-gray-200 rounded-t-lg flex items-center justify-center">
                <PlayCircle className="h-16 w-16 text-gray-400" />
              </div>
              <CardHeader className="pt-4">
                <CardTitle className="text-base">Network Topology Mapping</CardTitle>
                <CardDescription>Duration: 10:32</CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="text-sm text-gray-600 mb-3">
                  This tutorial shows how to use the network topology features to automatically
                  discover and map your network infrastructure.
                </p>
                <Button variant="outline" className="w-full">
                  <Play className="h-4 w-4 mr-2" />
                  Watch Tutorial
                </Button>
              </CardContent>
            </Card>
            
            <Card>
              <div className="aspect-video bg-gray-200 rounded-t-lg flex items-center justify-center">
                <PlayCircle className="h-16 w-16 text-gray-400" />
              </div>
              <CardHeader className="pt-4">
                <CardTitle className="text-base">Creating Custom Reports</CardTitle>
                <CardDescription>Duration: 15:05</CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="text-sm text-gray-600 mb-3">
                  Learn how to generate customized reports for different stakeholders,
                  schedule automated reports, and export data in various formats.
                </p>
                <Button variant="outline" className="w-full">
                  <Play className="h-4 w-4 mr-2" />
                  Watch Tutorial
                </Button>
              </CardContent>
            </Card>
            
            <Card>
              <div className="aspect-video bg-gray-200 rounded-t-lg flex items-center justify-center">
                <PlayCircle className="h-16 w-16 text-gray-400" />
              </div>
              <CardHeader className="pt-4">
                <CardTitle className="text-base">SNMP Configuration Guide</CardTitle>
                <CardDescription>Duration: 13:27</CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="text-sm text-gray-600 mb-3">
                  This tutorial covers SNMP setup, including community strings, security
                  settings, and troubleshooting common SNMP connectivity issues.
                </p>
                <Button variant="outline" className="w-full">
                  <Play className="h-4 w-4 mr-2" />
                  Watch Tutorial
                </Button>
              </CardContent>
            </Card>
            
            <Card>
              <div className="aspect-video bg-gray-200 rounded-t-lg flex items-center justify-center">
                <PlayCircle className="h-16 w-16 text-gray-400" />
              </div>
              <CardHeader className="pt-4">
                <CardTitle className="text-base">User Role Management</CardTitle>
                <CardDescription>Duration: 9:51</CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="text-sm text-gray-600 mb-3">
                  Learn how to configure user permissions, create custom roles, and
                  implement role-based access control for your monitoring system.
                </p>
                <Button variant="outline" className="w-full">
                  <Play className="h-4 w-4 mr-2" />
                  Watch Tutorial
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Contact Support */}
      <Card>
        <CardHeader>
          <CardTitle>Need More Help?</CardTitle>
          <CardDescription>
            Our support team is available to assist you with any questions or issues
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col md:flex-row gap-6">
          <div className="flex-1 bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center mb-3">
              <Mail className="h-5 w-5 text-primary mr-2" />
              <h3 className="font-medium">Email Support</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Send us an email and we'll get back to you within 24 hours.
            </p>
            <Button variant="outline" className="w-full">
              support@netmonitor.pro
            </Button>
          </div>
          
          <div className="flex-1 bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center mb-3">
              <MessageCircle className="h-5 w-5 text-primary mr-2" />
              <h3 className="font-medium">Community Forum</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Join our community forum to get help from other users and experts.
            </p>
            <Button variant="outline" className="w-full">
              Visit Forum
            </Button>
          </div>
          
          <div className="flex-1 bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center mb-3">
              <HeadphonesIcon className="h-5 w-5 text-primary mr-2" />
              <h3 className="font-medium">Live Support</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Chat with our support team for immediate assistance (business hours only).
            </p>
            <Button className="w-full">
              Start Live Chat
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}